import { type Key, useState } from "react";
import { Dialog as AriaDialog, Modal as AriaModal, ModalOverlay as AriaModalOverlay, type Selection } from "react-aria-components";
import { FilterLines, X, XClose } from "@untitledui/icons";
import { Button, MultiSelect, Select, cx } from "@tricura/atlas-ui";

/** "2 Years" → "2-years" — for building stable data-autotest ids from labels. */
function slugify(label: string | undefined): string {
    return (label ?? "").trim().toLowerCase().replace(/\s+/g, "-");
}

const YEAR_OPTIONS: { id: number; label: string }[] = [
    { id: 1, label: "1 Year" },
    { id: 2, label: "2 Years" },
    { id: 3, label: "3 Years" },
    { id: 4, label: "4 Years" },
    { id: 5, label: "5 Years" },
];

export interface HeatmapFilterState {
    type: Set<string>;
    facilityName: Set<string>;
    risk: Set<string>;
    county: Set<string>;
    state: Set<string>;
}

// eslint-disable-next-line react-refresh/only-export-components -- shared filter constant lives next to its consumer for now
export const EMPTY_FILTERS: HeatmapFilterState = {
    type: new Set<string>(),
    facilityName: new Set<string>(),
    risk: new Set<string>(),
    county: new Set<string>(),
    state: new Set<string>(),
};

// ─── Filters — a single button that opens a right-side drawer ─────────────────

interface HeatmapFiltersProps {
    filters: HeatmapFilterState;
    onChange: (next: HeatmapFilterState) => void;
    options: {
        type: string[];
        facilityName: string[];
        risk: string[];
        county: string[];
        state: string[];
    };
    /** Date-range window (years) — drives the litigation-data refetch.
     *  Per the OR-979 design it lives inside the Filters drawer. */
    year: number;
    onYearChange: (year: number) => void;
    /** Live count of rows matching the current filters — shown in the
     *  drawer footer ("N Results match these filters"). */
    resultCount: number;
    /** Custom attribute for E2E testing — applied to the Filters trigger button. */
    "data-autotest"?: string;
}

/** Count of filter categories with at least one active selection —
 *  rendered as a badge on the Filters button. The date range is always
 *  set, so it isn't counted. */
// eslint-disable-next-line react-refresh/only-export-components -- shared helper co-located with the drawer
export function countHeatmapFilters(f: HeatmapFilterState): number {
    return (Object.keys(f) as (keyof HeatmapFilterState)[]).reduce((n, k) => n + (f[k].size > 0 ? 1 : 0), 0);
}

/**
 * Filters button + right-side drawer (Legal / Claims heatmap). Per the
 * OR-979 design the filters are grouped under one button and laid out in
 * cards (Facility details · Risk level · Location · Date Range), applying
 * across both the map and the table. Filtering is client-side and live.
 */
export function HeatmapFilters({
    filters,
    onChange,
    options,
    year,
    onYearChange,
    resultCount,
    "data-autotest": dataAutotest,
}: HeatmapFiltersProps) {
    const [isOpen, setIsOpen] = useState(false);
    const count = countHeatmapFilters(filters);

    const update = (key: keyof HeatmapFilterState, next: Set<string>) => {
        onChange({ ...filters, [key]: next });
    };
    const clearAll = () => onChange({ ...EMPTY_FILTERS });

    return (
        <>
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                data-autotest={dataAutotest}
                className="border-primary bg-primary text-primary shadow-xs hover:bg-secondary inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm font-semibold transition duration-100 ease-linear"
            >
                <FilterLines aria-hidden="true" className="size-4 text-fg-quaternary stroke-[2.25px]" />
                Filters
                {count > 0 && (
                    <span className="bg-fg-brand-primary inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold leading-none text-white">
                        {count}
                    </span>
                )}
            </button>

            <AriaModalOverlay
                isOpen={isOpen}
                onOpenChange={setIsOpen}
                isDismissable
                className={({ isEntering, isExiting }) =>
                    cx(
                        // z-[1000] clears the Leaflet panes/controls (z-[500]) so
                        // the drawer isn't overlapped by the map (Andressa review).
                        "bg-overlay/70 fixed inset-0 z-[1000]",
                        isEntering && "animate-in fade-in ease-out",
                        isExiting && "animate-out fade-out ease-in",
                    )
                }
            >
                <AriaModal
                    className={({ isEntering, isExiting }) =>
                        cx(
                            "bg-primary outline-hidden fixed bottom-0 right-0 top-0 w-[420px] max-w-full shadow-2xl",
                            isEntering && "animate-in slide-in-from-right ease-out",
                            isExiting && "animate-out slide-out-to-right ease-in",
                        )
                    }
                >
                    <AriaDialog className="outline-hidden flex h-full flex-col">
                        {({ close }) => (
                            <>
                                <div className="border-secondary relative flex flex-col gap-0.5 border-b px-6 py-5">
                                    <h2 className="text-primary text-base font-semibold leading-6">Filters</h2>
                                    <p className="text-tertiary text-sm leading-5">Refine results across map and table.</p>
                                    <button
                                        type="button"
                                        onClick={close}
                                        aria-label="Close"
                                        data-autotest="legal-heatmap-filters-close-button"
                                        className="text-tertiary hover:text-primary size-8 hover:bg-secondary absolute right-3 top-3 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg"
                                    >
                                        <X aria-hidden="true" className="size-5 stroke-[2.25px]" />
                                    </button>
                                </div>

                                <div className="atlas-scrollbar bg-secondary flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
                                    <FilterCard title="Facility details">
                                        <FilterField
                                            label="Type"
                                            options={options.type}
                                            selected={filters.type}
                                            onChange={(next) => update("type", next)}
                                            data-autotest="legal-heatmap-filters-type-select"
                                        />
                                        <FilterField
                                            label="Facility Name"
                                            options={options.facilityName}
                                            selected={filters.facilityName}
                                            onChange={(next) => update("facilityName", next)}
                                            data-autotest="legal-heatmap-filters-facility-name-select"
                                        />
                                    </FilterCard>

                                    <FilterCard title="Risk level">
                                        <FilterField
                                            label="Risk"
                                            options={options.risk}
                                            selected={filters.risk}
                                            onChange={(next) => update("risk", next)}
                                            data-autotest="legal-heatmap-filters-risk-select"
                                        />
                                    </FilterCard>

                                    <FilterCard title="Location">
                                        <FilterField
                                            label="County"
                                            options={options.county}
                                            selected={filters.county}
                                            onChange={(next) => update("county", next)}
                                            data-autotest="legal-heatmap-filters-county-select"
                                        />
                                        <FilterField
                                            label="State"
                                            options={options.state}
                                            selected={filters.state}
                                            onChange={(next) => update("state", next)}
                                            data-autotest="legal-heatmap-filters-state-select"
                                        />
                                    </FilterCard>

                                    <FilterCard title="Date Range">
                                        <div className="flex flex-col gap-1.5">
                                            <span className="text-secondary text-sm font-medium">Date Range</span>
                                            <Select
                                                size="sm"
                                                aria-label="Date Range"
                                                data-autotest="legal-heatmap-filters-date-range-select"
                                                selectedKey={year}
                                                items={YEAR_OPTIONS}
                                                onSelectionChange={(key: Key | null) => {
                                                    if (key === null) return;
                                                    const n = Number(key);
                                                    if (Number.isFinite(n) && n !== year) onYearChange(n);
                                                }}
                                            >
                                                {(item) => <Select.Item id={item.id} data-autotest={`legal-heatmap-filters-date-range-${slugify(item.label)}`}>
                                                    {item.label}
                                                </Select.Item>}
                                            </Select>
                                        </div>
                                    </FilterCard>
                                </div>

                                <div className="border-secondary bg-primary flex items-center justify-between gap-2 border-t px-6 py-4">
                                    <div className="flex flex-col leading-tight">
                                        <span className="text-primary text-sm font-semibold">{resultCount} Results</span>
                                        <span className="text-tertiary text-xs">match these filters</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button color="secondary" size="sm" onClick={clearAll} data-autotest="legal-heatmap-filters-clear-all-button">
                                            Clear All
                                        </Button>
                                        {/* Disabled until at least one filter is selected
                                          * (Andressa review) — filtering is already live, so
                                          * this just dismisses the drawer. */}
                                        <Button color="primary" size="sm" isDisabled={count === 0} onClick={close} data-autotest="legal-heatmap-filters-show-results-button">
                                            Show results
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </AriaDialog>
                </AriaModal>
            </AriaModalOverlay>
        </>
    );
}

// ─── Card wrapper (groups related filter fields) ──────────────────────────────

function FilterCard({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="border-secondary bg-primary flex flex-col gap-3 rounded-xl border p-4">
            <h3 className="text-primary text-sm font-semibold">{title}</h3>
            {children}
        </section>
    );
}

// ─── Single filter trigger ───────────────────────────────────────────────────

interface FilterFieldProps {
    label: string;
    options: string[];
    selected: Set<string>;
    onChange: (next: Set<string>) => void;
    /** Custom attribute for E2E testing — applied to the MultiSelect trigger. */
    "data-autotest"?: string;
}

function FilterField({ label, options, selected, onChange, "data-autotest": dataAutotest }: FilterFieldProps) {
    const items = options.map((v) => ({ id: v, label: v }));
    const hasSelection = selected.size > 0;

    const handleSelectionChange = (sel: Selection) => {
        const next: Set<string> = sel === "all" ? new Set(options) : new Set(Array.from(sel, String));
        onChange(next);
    };

    const handleReset = () => onChange(new Set());
    const handleSelectAll = () => onChange(new Set(options));

    return (
        <div className="flex w-full flex-col gap-1.5">
            {/* Custom label row so we can hang the clear (X) button next to the
                label without fighting MultiSelect's internal trigger structure. */}
            <div className="flex items-center justify-between">
                <span className="text-secondary text-sm font-medium">{label}</span>
                {hasSelection && (
                    <button
                        type="button"
                        aria-label={`Clear ${label} filter`}
                        onClick={handleReset}
                        className="text-tertiary hover:text-secondary flex items-center gap-1 text-xs transition duration-100 ease-linear"
                    >
                        Clear
                        <XClose className="size-3" />
                    </button>
                )}
            </div>

            <MultiSelect
                size="sm"
                placeholder={`Select ${label}`}
                items={items}
                selectedKeys={selected}
                onSelectionChange={handleSelectionChange}
                onReset={handleReset}
                onSelectAll={handleSelectAll}
                data-autotest={dataAutotest}
            >
                {(item) => (
                    <MultiSelect.Item id={item.id} data-autotest={dataAutotest ? `${dataAutotest}-option` : undefined}>
                        {item.label}
                    </MultiSelect.Item>
                )}
            </MultiSelect>
        </div>
    );
}
